/**
 * Formatting only. Nothing in this file computes a figure - every number
 * passed in came out of `summary.json`, and everything returned is a string
 * (or a CSS-sized presentation of the same number). If a helper here ever
 * needs to combine two figures to produce a third, the contract is missing a
 * field (BUILD-SPEC 2).
 */

const SYMBOL: Record<string, string> = { EUR: "€", GBP: "£", USD: "$", PLN: "zł " };

export function money(n: number, currency: string): string {
  return `${SYMBOL[currency] ?? currency + " "}${n.toFixed(2)}`;
}

/**
 * A delta, signed. The sign is read off the number the file supplied;
 * `Math.abs` is presentation of that same number, not arithmetic on it.
 */
export function signedMoney(n: number, currency: string): string {
  return `${n < 0 ? "−" : "+"}${money(Math.abs(n), currency)}`;
}

export function pct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

/** Whole-percent form for dense rows: 0.1099 → "11%". */
export function pctShort(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

export function signedPct(p: number): string {
  return `${p < 0 ? "−" : "+"}${(Math.abs(p) * 100).toFixed(0)}%`;
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** "2026-07" → "July 2026". Falls back to the raw key rather than guessing. */
export function monthName(key: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(key);
  if (!m) return key;
  const name = MONTHS[Number(m[2]) - 1];
  return name ? `${name} ${m[1]}` : key;
}

/** "2026-07-23" → "23 Jul 2026". */
export function dateName(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const name = MONTHS[Number(m[2]) - 1];
  return name ? `${Number(m[3])} ${name.slice(0, 3)} ${m[1]}` : iso;
}

/**
 * The ten categories are a frozen physical set (BUILD-SPEC 4.2), so their
 * colours can be too. One hue each, stable across every screen.
 */
export const CATEGORY_COLOR: Record<string, string> = {
  "Produce": "#69c188",
  "Meat & Fish": "#e0736d",
  "Dairy & Eggs": "#e5c368",
  "Bakery": "#d19a66",
  "Pantry": "#b48ead",
  "Frozen": "#7fc4d8",
  "Snacks & Sweets": "#e894b0",
  "Beverages": "#5da2c4",
  "Household": "#9aa7b0",
  "Other": "#6f7f88",
};

export function categoryColor(name: string): string {
  return CATEGORY_COLOR[name] ?? "#6f7f88";
}
